/**
 * Ship Commit Dialog
 *
 * @module features/releases/components/PromoteCommitDialog
 * @description One dialog opened from a timeline node's ship control,
 * answering "what do I want to do here" — start a Release QA run, or promote
 * to an environment via a runbook. Distinct from `RunbookTriggerDialog`
 * (`apps/web/src/ui/components/property-tabs/RunbookTriggerDialog.tsx`): that
 * dialog is keyed to one already-chosen runbook and renders its declared
 * inputs. Here the open question is what to *do*, so the action picker comes
 * first.
 *
 * The dialog is opened from a specific commit's node, and both actions are
 * scoped to it: `Run release` starts a Release QA run at that commit's sha
 * (`useStartReleaseRun`), and the `Promote → <stage>` actions POST the same sha
 * to the runbook.
 *
 * File/export names kept as `PromoteCommitDialog` — `ReleaseTimeline.tsx`
 * imports this exact symbol and prop contract for its per-row "Promote"
 * affordance; renaming either would break that file. There is deliberately
 * no separate page-level instance — see `ReleasePropertyTimeline`.
 */

import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Banner,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTrigger,
  Flex,
  PillToggle,
  Text,
  TextField,
  useToast,
} from '@chatsdet/ui';
import { WarningCircle } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { useStartReleaseRun } from '../useReleases';
import type { PromotionRunbook, RunbookRun } from '../../runbooks/types';
import { shortSha } from '../../runbooks/types';

export type PromotableCommit = {
  sha: string;
  message: string;
};

interface PromoteCommitDialogProps {
  commit: PromotableCommit | null;
  /** The available promotions for this property — build with `isPromotionRunbook`. */
  promotions: PromotionRunbook[];
  propertyId: string;
  onOpenChange: (open: boolean) => void;
}

/** Sentinel id for the non-promotion "run the regression suite" action. */
const RUN_TESTS_ACTION = '__run_tests__';

function stageLabel(stage: string): string {
  return stage.charAt(0).toUpperCase() + stage.slice(1);
}

export function PromoteCommitDialog({
  commit,
  promotions,
  propertyId,
  onOpenChange,
}: PromoteCommitDialogProps) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedAction, setSelectedAction] = React.useState<string>(RUN_TESTS_ACTION);
  const [confirmText, setConfirmText] = React.useState('');

  React.useEffect(() => {
    if (commit) {
      // "Run tests" is never destructive, so it's always the safe default —
      // the operator only has to make an explicit pick when they actually
      // want to promote.
      setSelectedAction(RUN_TESTS_ACTION);
      setConfirmText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit?.sha]);

  // The typed confirmation is a deliberate act for the stage about to ship —
  // it must not survive an action switch (type "prod", flip to "dev", flip
  // back to "prod" should not leave Run enabled without re-typing).
  React.useEffect(() => {
    setConfirmText('');
  }, [selectedAction]);

  const selectedRunbook = promotions.find((r) => r.id === selectedAction) ?? null;
  const isRunTests = selectedAction === RUN_TESTS_ACTION;
  // Both actions are scoped to `commit`, so the title names it either way.
  const dialogTitle = !commit
    ? 'Run release'
    : isRunTests
      ? `Run release on ${shortSha(commit.sha)}`
      : `Ship ${shortSha(commit.sha)}`;
  const confirmRequired = selectedRunbook?.destructive ?? false;
  const confirmSatisfied = !confirmRequired || confirmText === selectedRunbook?.slug;
  const canRun = isRunTests ? true : !!selectedRunbook && confirmSatisfied;

  const startRunMutation = useStartReleaseRun();

  const promoteMutation = useMutation({
    mutationFn: async (): Promise<{ run: RunbookRun; stage: string; shortSha: string }> => {
      if (!commit || !selectedRunbook) throw new Error('No stage selected');
      const res = await fetch(`${API_URL}/org/runbooks/${selectedRunbook.id}/run`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          inputs: {},
          sha: commit.sha,
          // The server now enforces the same typed-confirmation gate for
          // destructive runbooks (422 RUNBOOK_CONFIRMATION_REQUIRED) — this
          // isn't vestigial, don't drop it.
          ...(confirmRequired ? { confirm: confirmText } : {}),
        }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to start promotion');
      const data: { ok: boolean; run: RunbookRun } = await res.json();
      return { run: data.run, stage: selectedRunbook.stage, shortSha: shortSha(commit.sha) };
    },
    onSuccess: ({ stage, shortSha: sha }) => {
      qc.invalidateQueries({ queryKey: ['property-runbook-runs', propertyId] });
      toast.success(`Promoting ${sha} to ${stage}`);
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to start promotion');
    },
  });

  const isPending = promoteMutation.isPending || startRunMutation.isPending;

  const handleRun = () => {
    if (isRunTests) {
      startRunMutation.mutate(
        { propertyId, sha: commit?.sha },
        {
          onSuccess: (result) => {
            toast.success('Release started');
            onOpenChange(false);
            navigate({
              to: '/releases/$propertyId',
              params: { propertyId },
              search: { runId: result.run.releaseRunId },
            });
          },
          onError: (error) => {
            toast.error(error instanceof Error ? error.message : 'Failed to start release run');
          },
        }
      );
      return;
    }
    promoteMutation.mutate();
  };

  return (
    <DialogTrigger
      isOpen={!!commit}
      onOpenChange={(open) => {
        if (!open) onOpenChange(false);
      }}
      size="2xl"
    >
      <span />
      <Dialog
        title={dialogTitle}
        // No description: the commit is on the next line and the actions are
        // named on the buttons, so a sentence restating both only added height.
        className="ship-commit-dialog"
        footer={
          <>
            <Button variant="ghost" onPress={() => onOpenChange(false)} isDisabled={isPending}>
              Cancel
            </Button>
            <Button
              variant={confirmRequired ? 'destructive' : 'primary'}
              onPress={handleRun}
              isDisabled={!canRun || isPending}
            >
              {isPending ? 'Working…' : 'Run'}
            </Button>
          </>
        }
      >
        <DialogContent>
          {/* Plain div rather than `Flex`: `Flex` does not forward a width
              class, so this column sized to its widest nowrap descendant and
              overflowed the dialog's grid track. */}
          <div className="flex w-full min-w-0 flex-col gap-4">
            {/* Always shown so the dialog keeps its height when switching
                actions; both actions are scoped to this commit. */}
            {commit && (
              // A plain div, not `Flex`: `Flex` does not forward a width class,
              // so the row sized to its nowrap content (460px inside a 406px
              // grid track) and a long subject ran past the dialog edge, where
              // the dialog's own overflow clipped it with no ellipsis.
              <div className="flex w-full min-w-0 items-baseline gap-2">
                <Text size="sm" weight="medium" className="shrink-0 font-mono">
                  {shortSha(commit.sha)}
                </Text>
                <Text size="sm" className="min-w-0 flex-1 truncate text-muted-foreground">
                  {commit.message}
                </Text>
              </div>
            )}

            <PillToggle
              // The toggle is `inline-flex`, but this column stretches its
              // children by default, which pulled the track to the dialog's
              // full width and left the three pills floating in a long
              // empty rail. `self-start` restores its intrinsic width.
              className="self-start"
              aria-label="Action"
              value={selectedAction}
              onChange={setSelectedAction}
              options={[
                { value: RUN_TESTS_ACTION, label: 'Run release' },
                ...promotions.map((runbook) => ({
                  value: runbook.id,
                  label: `Promote → ${stageLabel(runbook.stage)}`,
                })),
              ]}
            />

            {isRunTests && (
              <Banner variant="info" className="py-1.5">
                <Text size="xs">
                  Starts the release agent on {commit ? shortSha(commit.sha) : 'this commit'}. It
                  runs the regression suites on this commit, reviews the changes since the last
                  release, writes release notes, and promotes{' '}
                  {commit ? shortSha(commit.sha) : 'this commit'} only if the agent is allowed to.
                </Text>
              </Banner>
            )}

            {confirmRequired && selectedRunbook && (
              <Flex
                direction="column"
                gap="xs"
                className="rounded-md border border-destructive/30 bg-destructive/5 p-3"
              >
                <Flex align="center" gap="xs">
                  <WarningCircle size={16} className="text-destructive shrink-0" />
                  <Text size="sm" weight="medium">
                    This runbook is marked destructive.
                  </Text>
                </Flex>
                <Box>
                  <Text size="sm" className="text-muted-foreground mb-1 block">
                    Type{' '}
                    <Text weight="bold" as="span">
                      {selectedRunbook.slug}
                    </Text>{' '}
                    to confirm
                  </Text>
                  <TextField
                    value={confirmText}
                    onChange={setConfirmText}
                    placeholder={selectedRunbook.slug}
                    autoFocus
                  />
                </Box>
              </Flex>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </DialogTrigger>
  );
}
